import { LitElement, html } from "lit";
import { customElement, property } from "lit/decorators.js";
import { Task } from "@lit/task";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { fetchApi, types } from "../api/index.js";
import { apiLink } from "../utils/format.js";
import "../components/loading-spinner.js";
import "../components/error-message.js";
import "../components/copy-button.js";

@customElement("overview-tab")
export class OverviewTab extends LitElement {
  @property() imageName = "";

  createRenderRoot() {
    return this;
  }

  private _task = new Task(this, {
    args: () => [this.imageName] as const,
    task: ([name]) => fetchApi<types.ImageResponse>(`/v1/images/${name}`),
  });

  render() {
    return html`
      ${this._task.render({
        pending: () => html`<loading-spinner></loading-spinner>`,
        error: (e) => html`<error-message .message=${String(e)}></error-message>`,
        complete: (data) => html`
          <div class="space-y-6">
            <div class="space-y-3 relative">
              <div class="absolute top-0 right-0">${apiLink(`/v1/images/${this.imageName}`)}</div>
              ${data.streams.length > 1 || data.deprecated_streams?.length
                ? html`
                    <div>
                      <h3 class="text-gray-400 text-xs uppercase tracking-wide mb-2">Streams</h3>
                      <div class="flex flex-wrap gap-1">
                        ${data.streams.map(
                          (s) => html`
                            <span
                              class="px-1.5 py-0.5 text-xs rounded whitespace-nowrap ${data.deprecated_streams?.includes(s)
                                ? "bg-yellow-500/20 text-yellow-400 border border-yellow-500/30"
                                : "bg-hb-bg text-blue-400/70 border border-blue-400/30"}"
                              title=${data.deprecated_streams?.includes(s) ? "Deprecated stream" : ""}
                            >${s}${data.deprecated_streams?.includes(s) ? " (deprecated)" : ""}</span>
                          `,
                        )}
                      </div>
                    </div>
                  `
                : ""}

              ${data.variants.length
                ? html`
                    <div>
                      <h3 class="text-gray-400 text-xs uppercase tracking-wide mb-2">Variants</h3>
                      <div class="flex flex-wrap gap-1">
                        ${data.variants.map(
                          (v) => html`
                            <span
                              class="px-1.5 py-0.5 text-xs rounded bg-hb-bg text-hb-accent/70 border border-hb-accent/30 whitespace-nowrap"
                              title=${v.description}
                            >${v.name}</span>
                            ${v.builder
                              ? html`<span class="px-1 py-0.5 text-xs rounded bg-hb-bg text-green-400/70 border border-green-400/30 whitespace-nowrap -ml-0.5">builder</span>`
                              : ""}
                            ${v.fips
                              ? html`<span class="px-1 py-0.5 text-xs rounded bg-hb-bg text-yellow-400/70 border border-yellow-400/30 whitespace-nowrap -ml-0.5">fips</span>`
                              : ""}
                          `,
                        )}
                      </div>
                    </div>
                  `
                : ""}
            </div>

            <div>
              <h3 class="text-gray-400 text-xs uppercase tracking-wide mb-2">README</h3>
              <div class="prose prose-invert prose-sm max-w-none bg-hb-surface border border-hb-border rounded-lg p-4">
                ${unsafeHTML(data.readme_html)}
              </div>
            </div>

            ${data.licenses?.length
              ? html`
                  <div>
                    <h3 class="text-gray-400 text-xs uppercase tracking-wide mb-2">Licenses</h3>
                    <div class="flex flex-wrap gap-1">
                      ${data.licenses.map(
                        (l) =>
                          html`<span class="px-1.5 py-0.5 text-xs rounded bg-hb-bg text-gray-400 border border-hb-border whitespace-nowrap">${l}</span>`,
                      )}
                    </div>
                  </div>
                `
              : ""}
          </div>
        `,
      })}
    `;
  }
}
